import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Play, Loader, AlertTriangle, Eraser, Palette } from 'lucide-react';

import { getBQTablePreview, launchBulkTreatment, uploadReferenceImagesBulk } from 'services/apiService';

interface PreviewPanelProps {
  projectId: string;
  config: BigQueryConfig;
  selectedColumns: string[];
  imageColumn: string | null;
  promptRules: PromptRule[];
  treatmentName: string;
  onBack: () => void;
  mediaType: MediaType;
  removeBackground: boolean;
}

interface BigQueryConfig {
  project_id: string;
  dataset_id: string;
  table_name: string;
}

interface PreviewRow {
  [key: string]: any;
}

const PreviewPanel: React.FC<PreviewPanelProps> = ({
  projectId,
  config,
  selectedColumns,
  imageColumn,
  promptRules,
  treatmentName,
  onBack,
  mediaType,
  removeBackground
}) => {
  const navigate = useNavigate();
  const [previewData, setPreviewData] = useState<PreviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rowCount, setRowCount] = useState<number>(0);
  const [matchingRowCount, setMatchingRowCount] = useState<number>(0);

  const fetchPreview = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getBQTablePreview(
        config.project_id,
        config.dataset_id,
        config.table_name,
        selectedColumns.concat(imageColumn ? [imageColumn] : []),
        promptRules,
        projectId
      );
      const matchingRows: any[] = data.rows || [];
      
      setPreviewData(matchingRows.slice(0, 5)); // Show first 5 matching rows
      setRowCount(data.total_rows_with_skipped || 0);
      setMatchingRowCount(data.total_rows); // Actual count from preview
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [config.dataset_id, config.project_id, config.table_name, imageColumn, promptRules, projectId, selectedColumns]);

  useEffect(() => {
    fetchPreview();
  }, [fetchPreview]);

  const evaluateCondition = (row: PreviewRow, rule: PromptRule): boolean => {
    const { column, operator, value } = rule.condition;
    const rowValue = String(row[column] || '');
    
    switch (operator) {
      case '=':
        return rowValue === value;
      case '!=':
        return rowValue !== value;
      case 'contains':
        return rowValue.toLowerCase().includes(value.toLowerCase());
      case 'starts_with':
        return rowValue.toLowerCase().startsWith(value.toLowerCase());
      case 'ends_with':
        return rowValue.toLowerCase().endsWith(value.toLowerCase());
      case '>':
        return parseFloat(rowValue) > parseFloat(value);
      case '<':
        return parseFloat(rowValue) < parseFloat(value);
      case '>=':
        return parseFloat(rowValue) >= parseFloat(value);
      case '<=':
        return parseFloat(rowValue) <= parseFloat(value);
      default:
        return false;
    }
  };

  const getPromptForRow = (row: PreviewRow): { prompt: string; rule: PromptRule } | null => {
    // Find first matching rule - return null if no match
    for (const rule of promptRules) {
      if (evaluateCondition(row, rule)) {
        return { prompt: rule.prompt, rule };
      }
    }
    
    // No matching rule - this row will be skipped
    return null;
  };

  const interpolatePrompt = (prompt: string, row: PreviewRow): string => {
    let result = prompt;
    selectedColumns.forEach(col => {
      const regex = new RegExp(`\\{${col}\\}`, 'g');
      result = result.replace(regex, String(row[col] || ''));
    });
    return result;
  };

  const handleLaunch = async () => {
    setLaunching(true);
    setError(null);

    if (!imageColumn) {
      setError('Image column is required to launch the treatment.');
      setLaunching(false);
      return;
    }

    // Upload the pictures and launch the treatment
    // Flatten pictures

    const allReferenceImagesFiles = promptRules
      .flatMap(rule => rule.referenceImages || [])
      .map(img => img.file)
    // Replace the base64 pictures by their URLs before sending to the backend


    try {
      let uploadResults: Record<string, string> = {};
      if (allReferenceImagesFiles.length > 0) {
        uploadResults = (await uploadReferenceImagesBulk(allReferenceImagesFiles, projectId)).uploads;
      }

      const updatedPromptRules: APIPromptRule[] = promptRules.map(rule => {
        const updatedReferenceImages = (rule.referenceImages || [])
          .map(img => ({
            uri: uploadResults[img.file.name] || img.url,
            category: img.category
          }));
        return {
          ...rule,
          referenceImages: updatedReferenceImages
        };
      });

      await launchBulkTreatment(
        config.project_id,
        config.dataset_id,
        config.table_name,
        selectedColumns,
        updatedPromptRules,
        imageColumn,
        mediaType,
        treatmentName,
        projectId,
        removeBackground
      );

      navigate(`/projects/${projectId}/bulk-treatments`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to launch treatment');
    } finally {
      setLaunching(false);
    }
  };

  const mediaLabel = mediaType === 'image' ? 'Image' : 'Video';
  const mediaEmoji = mediaType === 'image' ? '📷' : '🎬';
  const mediaBorderColor = mediaType === 'image' ? 'purple' : 'blue';

  // Background removal only applies to images
  const effectiveRemoveBackground = mediaType === 'image' && removeBackground;

  // Determine if this is background-removal only (no prompts)
  const isBackgroundRemovalOnly = effectiveRemoveBackground && promptRules.length === 0;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader className="w-8 h-8 text-blue-400 animate-spin mb-4" />
        <p className="text-gray-400">Loading preview...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Eye className="w-6 h-6 text-green-400" />
          <h2 className="text-xl font-semibold text-white">Preview & Launch</h2>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-red-300 font-medium">Error</p>
            <p className="text-xs text-red-400 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Warning if no rows match - only show if we have prompt rules */}
      {matchingRowCount === 0 && promptRules.length > 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-yellow-300 font-medium">No matching rows</p>
            <p className="text-xs text-yellow-400 mt-1">
              No rows match your defined rules. Please adjust your conditions.
            </p>
          </div>
        </div>
      )}

      {/* Treatment Summary */}
      <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-300 mb-3">Treatment Summary</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-400">Table:</span>
            <span className="text-white font-mono text-xs">
              {config.project_id}.{config.dataset_id}.{config.table_name}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Input Image Column:</span>
            <span className={`text-purple-400 font-mono text-xs`}>
              {imageColumn || 'None'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Media Type:</span>
            <span className="text-white">{mediaEmoji} {mediaLabel}s</span>
          </div>
          {effectiveRemoveBackground && (
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Background Removal:</span>
              <span className="text-emerald-400 flex items-center gap-1.5">
                <Eraser className="w-3.5 h-3.5" />
                Enabled
              </span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-gray-400">Segmentation Rules:</span>
            <span className="text-white">
              {promptRules.length > 0 
                ? `${promptRules.length} rule${promptRules.length > 1 ? 's' : ''}`
                : <span className="text-gray-500">None (all rows)</span>
              }
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Rows to process:</span>
            <span className="text-green-400 font-semibold">
              {isBackgroundRemovalOnly ? rowCount.toLocaleString() : matchingRowCount.toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      {/* Preview Examples */}
      <div>
        <h3 className="text-sm font-semibold text-gray-300 mb-3">
          Preview ({previewData.length} examples)
        </h3>
        {previewData.length === 0 ? (
          <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-8 text-center">
            <p className="text-gray-400">No matching rows</p>
          </div>
        ) : (
          <div className="space-y-3">
            {previewData.map((row, idx) => {
              const result = getPromptForRow(row);
              if (!result) return null;
              
              const { prompt, rule } = result;
              const interpolated = interpolatePrompt(prompt, row);
              const imageUrl = imageColumn ? row[imageColumn] : null;
              const referenceImages = rule.referenceImages || [];
              
              return (
                <div 
                  key={idx}
                  className="bg-gray-900/50 border border-gray-700 rounded-lg p-4 hover:border-gray-600 transition-colors"
                >
                  {/* Header */}
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-gray-500 font-semibold">ROW {idx + 1}</span>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs px-2 py-1 bg-purple-500/20 border border-purple-500/30 text-purple-300 rounded font-mono">
                        {rule.condition.column} = "{rule.condition.value}"
                      </span>
                      {/* Rule-specific Background Removal Badge */}
                      {rule.removeBackground && (
                        <span className="text-xs px-2 py-1 bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 rounded flex items-center gap-1">
                          <Eraser className="w-3 h-3" />
                          BG Remove
                        </span>
                      )}
                      {/* Rule-specific Brand Guidelines Badge */}
                      {rule.useBrandGuidelines && (
                        <span className="text-xs px-2 py-1 bg-amber-500/20 border border-amber-500/30 text-amber-300 rounded flex items-center gap-1">
                          <Palette className="w-3 h-3" />
                          Brand
                        </span>
                      )}
                      <span className={`text-xs px-2 py-1 bg-${mediaBorderColor}-500/20 border border-${mediaBorderColor}-500/30 text-${mediaBorderColor}-300 rounded flex items-center gap-1`}>
                        {mediaEmoji} → {mediaType === 'image' ? 'New Image' : 'Video'}
                      </span>
                    </div>
                  </div>

                  {/* Main Content - Horizontal Layout */}
                  <div className="grid grid-cols-12 gap-4">
                    {/* Left: Image */}
                    {imageUrl && (
                      <div className="col-span-12 md:col-span-3">
                        <div className="bg-black/30 rounded-lg p-2 h-full">
                          <div className="text-xs text-purple-400 mb-2 font-medium">📷 Input</div>
                          <img 
                            src={String(imageUrl)} 
                            alt="Input Preview" 
                            className="w-full aspect-square object-cover rounded border border-gray-600"
                            onError={(e) => {
                              const target = e.currentTarget;
                              target.style.display = 'none';
                              const fallback = target.nextElementSibling as HTMLElement;
                              if (fallback) fallback.classList.remove('hidden');
                            }}
                          />
                          <div className="hidden text-xs text-gray-500 font-mono break-all mt-2">
                            {String(imageUrl)}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Right: Data & Prompt */}
                    <div className={`${imageUrl ? 'col-span-12 md:col-span-9' : 'col-span-12'} space-y-3`}>
                      {/* Column Values - Compact Grid */}
                      {selectedColumns.length > 0 && (
                        <div className="bg-black/20 rounded-lg p-3">
                          <div className="text-xs text-gray-400 font-medium mb-2">Data</div>
                          <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1.5 text-xs">
                            {selectedColumns.map(col => (
                              <div key={col} className="flex items-start gap-1">
                                <span className="text-gray-500 shrink-0">{col}:</span>
                                <span className="text-gray-200 font-mono truncate" title={String(row[col] || 'N/A')}>
                                  {String(row[col] || 'N/A')}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Generated Prompt */}
                      <div className="bg-black/20 rounded-lg p-3">
                        <div className="text-xs text-gray-400 font-medium mb-2">Generated Prompt</div>
                        <div className="text-sm text-gray-300 font-mono leading-relaxed">
                          {interpolated}
                        </div>
                      </div>

                      {/* Reference Images - Single Row Thumbnails */}
                      {referenceImages.length > 0 && (
                        <div className="bg-black/20 rounded-lg p-3">
                          <div className="text-xs text-purple-400 mb-2 font-medium flex items-center gap-1">
                            <span>🖼️ Reference Images</span>
                            <span className="text-gray-500 text-[10px]">({referenceImages.length})</span>
                          </div>
                          
                          <div className="flex flex-row flex-wrap gap-2">
                            {referenceImages.map((referenceImage, index) => (
                              <div key={index} className="relative group">
                                <img 
                                  src={String(referenceImage.url)} 
                                  alt={`Reference ${index + 1}`} 
                                  className="w-12 h-12 object-cover rounded border border-gray-700 hover:border-purple-500 transition-colors"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Launch Button */}
      <div className="flex gap-3 pt-4">
        <button
          onClick={onBack}
          disabled={launching}
          className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 px-4 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Back
        </button>
        <button
          onClick={handleLaunch}
          disabled={launching || matchingRowCount === 0}
          className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-300 hover:shadow-xl hover:shadow-green-500/40 hover:scale-[1.02] active:scale-[0.98] disabled:from-gray-700 disabled:to-gray-600 disabled:shadow-none disabled:cursor-not-allowed disabled:scale-100 flex items-center justify-center gap-2"
        >
          {launching ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              Launching...
            </>
          ) : (
            <>
              <Play className="w-5 h-5" />
              Launch Treatment
            </>
          )}
        </button>
      </div>

      {/* Info Banner - Updated */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
        <p className="text-sm text-blue-300">
          <strong>Note:</strong> This treatment will process{' '}
          {isBackgroundRemovalOnly 
            ? `${rowCount.toLocaleString()} images with background removal`
            : `${matchingRowCount.toLocaleString()} ${mediaType === 'image' ? 'images' : 'videos'}${effectiveRemoveBackground ? ' with background removal' : ''}`
          }
          {' '}in the background.
          {!isBackgroundRemovalOnly && rowCount - matchingRowCount > 0 && ` ${(rowCount - matchingRowCount).toLocaleString()} rows that don't match any rule will be skipped.`}
          {' '}You'll receive a notification when it's complete.
        </p>
      </div>
    </div>
  );
};

export default PreviewPanel;
